import { alarms, temp_schemes } from '../lists/scenes-derived';
import { devices } from '../lists/devices';
import { groups } from '../lists/groups';
import { home_data, user_data, user_settings } from '../account/user';
import { rooms } from '../lists/rooms';
import { derived, get, writable } from 'svelte/store';

import { scenes } from '../lists/scenes';
import { sortByPosition } from '../../components/Lists/list-helpers';
import { weather_store } from 'src/helpers/geolocalion_helper';
import { dashboardWeatherItem } from 'src/components/Weather/helpers/dashboard_status_helpers';
import { format } from 'svelte-i18n';
import { hasCameraDevices } from 'src/components/Camera/helpers/camera-helpers';
import { getCameraDashboardTabs, prependLegacyActivityLogTab } from '../toDelete/legacy-dashboard-tabs';
import { showNewLayout } from '../toDelete/new_layout';
import { getIsEnergyMonitoringVisible } from 'src/components/Lists/list-helpers';
import { PREDEFINED_DASHBOARD_IDS } from 'src/components/Dashboards/dash-energy-types';
import { allEnergyWidgetsGalleryEnabled } from 'src/stores/features/dev/all-energy-widgets-gallery';
import { allDeviceWidgetsGalleryEnabled } from 'src/stores/features/dev/all-device-widgets-gallery';

const isStoredSystemDashboardKey = (key: string) =>
	PREDEFINED_DASHBOARD_IDS.includes(Number(key) as (typeof PREDEFINED_DASHBOARD_IDS)[number]);

// type DashboardsStore = {
// 	subscribe: Writable<number>["subscribe"]

// 	update: () => void
// 	remove: () => void
// 	reset: () => void
//   }
export const dashesSearchValue = writable('');

const defaultDash = {
	id: -1,
	name: 'My Dashboard',
	position: 2,
	notification: null,
	text: 'My Dashboard',
	route: `/-1`,
	devices: [],
	rooms: [],
	groups: [],
	scenes: [],
	alarms: [],
	temp_schemes: [],
	image: '',
	weather: {},
};

function createDashboards() {
	const { subscribe, set, update } = writable(JSON.parse(localStorage.getItem('dashboards')) || {});

	return {
		subscribe,
		set,
		update: (new_dashboards_data) =>
			update((dashboards) => {
				return { ...dashboards, ...new_dashboards_data };
			}),
		remove: (id) =>
			update((dashboards) => {
				delete dashboards[id];
				return dashboards;
			}),
		reset: () => set({}),
	};
}

export const dashboards = createDashboards();

dashboards.subscribe((val) => {
	if (Object.keys(val).length == 0) {
		val['-1'] = defaultDash;
	}
	localStorage.setItem('dashboards', JSON.stringify(val));
});

// Recomputes when any list store or premium/weather inputs change. Splitting deps would
// require per-widget derived stores — deferred unless profiling shows this as a bottleneck.
export const extended_dashboards = derived(
	[
		dashboards,
		devices,
		rooms,
		groups,
		scenes,
		alarms,
		temp_schemes,
		weather_store,
		user_data,
		user_settings,
	],
	([
		$dashboards,
		$devices,
		$rooms,
		$groups,
		$scenes,
		$alarms,
		$temp_schemes,
		$weather_store,
		$user_data,
		$user_settings,
	]) => {
		const extended_dashboards = {};

		let lists = {
			devices: $devices,
			rooms: $rooms,
			groups: $groups,
			scenes: $scenes,
			alarms: $alarms,
			temp_schemes: $temp_schemes,
			weather: {},
		};

		for (const dash_id in $dashboards) {
			if (Object.prototype.hasOwnProperty.call($dashboards, dash_id)) {
				const dash_data = $dashboards[dash_id];

				if (typeof extended_dashboards[dash_id] == 'undefined') {
					extended_dashboards[dash_id] = [];
				}
				for (const type in dash_data) {
					if (
						Object.prototype.hasOwnProperty.call(dash_data, type) &&
						typeof lists[type] !== 'undefined'
					) {
						const type_list = dash_data[type];
						for (const id in type_list) {
							if (Object.prototype.hasOwnProperty.call(type_list, id)) {
								const e = type_list[id];
								let dashItem;
								if (type === 'weather') {
									const isPremium = $user_data.premium;
									const wasPremium = $user_settings.been_premium;
									const hasWeatherData = !!$weather_store;

									const shouldShowWeather =
										(isPremium && hasWeatherData) ||
										(isPremium && !hasWeatherData) ||
										(!isPremium && wasPremium);

									if (isPremium && hasWeatherData) {
										dashItem = dashboardWeatherItem(type, $weather_store, {
											...e,
											id: id,
										});
									} else if (shouldShowWeather) {
										dashItem = {
											_type: type,
											position: e.position,
											id: id || 'wc-0',
											details: [],
											image: '',
											temperature: '',
											temperaturePrefix: '',
											icon: '',
											type: '',
											name: get(format)('dashboard_weather_widget_card', {
												default: 'Weather Widget',
											}),
										};
									}
								} else if (lists[type][id] !== undefined) {
									// NOTICE: Skip showing widgets for elements that do not exist in the interface lists stores
									//  (e.g. deleted devices/groups/scenes/etc with remained widgets in the dashboards)
									dashItem = {
										...lists[type][id],
										_type: type,
										position: e.position,
										...(type === 'scenes' && { id }),
										id: id,
									};
								}

								if (dashItem) {
									extended_dashboards[dash_id] = [
										...extended_dashboards[dash_id],
										dashItem,
									];
								}
							}
						}
					}
				}
			}
		}
		return extended_dashboards;
	},
	{},
);

export const dashboardTabs = derived(
	[
		dashboards,
		devices,
		home_data,
		user_data,
		allEnergyWidgetsGalleryEnabled,
		allDeviceWidgetsGalleryEnabled,
	],
	([$dashboards, $devices, $home_data]) => {
		const dashboardTabsSort = Object.keys($dashboards)
			.filter((key) => !isStoredSystemDashboardKey(key))
			.map((key) => {
				return {
					id: $dashboards[key].id,
					key: $dashboards[key].name,
					position: $dashboards[key].position,
					notification: null,
					text: $dashboards[key].name,
					image: $dashboards[key].image,
					backgroundColor: $dashboards[key].backgroundColor,
					icon: $dashboards[key].icon,
					route: `/${key}`,
				};
			})
			.sort(sortByPosition);

		const systemTabOptions = {
			showEnergy: get(showNewLayout) && getIsEnergyMonitoringVisible($home_data),
			home_data: $home_data,
		};

		// New layout: system dashboards are shown in the Tabs rail even when empty.
		if (get(showNewLayout) || hasCameraDevices($devices)) {
			return getCameraDashboardTabs(dashboardTabsSort, systemTabOptions);
		}

		return prependLegacyActivityLogTab(dashboardTabsSort);
	},
);
